import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { CategoryCard } from '../../components/cards/categoryCard'
import CountCard from '../../components/cards/CountCard'
import { ImageBanner } from '../../components/images/imageBanner'
import { EditInput } from '../../components/inputs/editInput'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { ProductTileContainer } from '../../components/product/productTile'
import Spinner from '../../components/spinner/Spinner'
import { TableComponent } from '../../components/table'
import { HeaderSubheader } from '../../components/typography/HeaderSubheader'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import Layout from '../../layout'
import {
  useDeleteListMutation,
  useFetchListCommentsQuery,
  useFetchListQuery,
  useGetListProductsQuery,
} from '../../redux-toolkits/lists/list.slice'

export const SingleEssentialPage: React.FC = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { id } = useParams<{ id: string }>()

  const [query, setQuery] = useState({
    page: 1,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
  })

  const { data: listInfo } = useFetchListQuery(
    { id: id ?? '' },
    {
      skip: !id,
    },
  )

  const { data, isFetching } = useFetchListCommentsQuery({
    id: id ?? '',
    query,
  })

  const { data: listProducts, isFetching: fetchingList } =
    useGetListProductsQuery(id || '')

  const [deleteList, { isLoading: deleting }] = useDeleteListMutation()

  return (
    <Layout>
      {!isFetching ? (
        <div className="flex flex-col gap-10">
          <BackButton />

          <div className="flex flex-wrap items-center justify-between gap-4">
            <PageHeader
              title="Essential Details and Metrics"
              className="!mb-0"
            />

            <div className="flex justify-end items-center gap-4">
              <Button
                label="Edit"
                primary={false}
                size="medium"
                className="!px-10"
                onClick={() => {
                  navigate(`/lists/curated/edit/${id}`)
                }}
              />

              <Button
                label="Delete"
                primary
                size="medium"
                className="!px-10 !bg-danger !border-transparent"
                onClick={() => {
                  setOpen(true)
                }}
              />
            </div>
          </div>

          <div className="relative">
            <ImageBanner
              imageUrl={listInfo?.data.banner ?? ''}
              loading={false}
              className=""
            />

            <ImageBanner
              imageUrl={listInfo?.data.icon ?? ''}
              loading={false}
              className="max-w-[80px] max-h-[80px] !rounded-full !absolute left-4 -bottom-7"
            />
          </div>

          <div className="flex flex-col gap-4">
            <EditInput
              title={`Title`}
              value={listInfo?.data.title ?? ''}
              type="text"
              editing={false}
              onEditClick={undefined}
              onEditSave={() => {}}
              onCancel={() => {}}
              className="border-b pb-4"
            />

            <EditInput
              title={`Description`}
              value={listInfo?.data.summary ?? ''}
              type="text"
              editing={false}
              onEditClick={undefined}
              onEditSave={() => {}}
              onCancel={() => {}}
              className="border-b pb-4"
            />
          </div>

          <div className="flex flex-col gap-4">
            <HeaderSubheader title="Categories" />

            <div className="flex items-center flex-wrap gap-2">
              {listInfo?.data.category.map((cat, idx) => {
                const key = `${cat._id}-${idx}`

                return <CategoryCard key={key} category={cat} />
              })}
            </div>
          </div>

          <div className="flex flex-col gap-6  pb-10">
            <HeaderSubheader
              title="Products"
              text="Collection of products in this essential"
              className=""
            />

            <ProductTileContainer
              products={listProducts?.data?.products ?? []}
              handleSortEnd={() => {}}
              canDelete={false}
              onDeleteProduct={(p, idx) => {}}
              sortable={false}
              onViewProduct={(p) => {
                navigate(`/products/view/${p?._id}`)
              }}
            />
          </div>

          <div className="flex flex-col gap-6">
            <HeaderSubheader title="Essential Impressions" />

            <CountCardContainer className="!my-0">
              <CountCard
                count={0}
                text="Total impressions"
                isCurrency={false}
              />
            </CountCardContainer>

            <HeaderSubheader title="Comments" />

            <TableComponent
              headers={['Name', 'Email', 'Message']}
              rows={
                data?.data?.docs?.map((item) => {
                  return {
                    id: item._id,
                    content: [item.name, item.email, item.message],
                  }
                }) ?? []
              }
              loading={isFetching}
              isEmpty={[0, undefined].includes(data?.data?.docs?.length)}
              currentPage={query?.page}
              totalDataCount={data?.data?.total ?? 0}
              pageRangeDisplayed={3}
              onPageChange={(pageNo) => {
                setQuery({ ...query, page: pageNo })
              }}
            />
          </div>
        </div>
      ) : (
        <Spinner fullScreen={false} />
      )}

      <ConfirmationModal
        title="Delete Essential"
        open={open}
        setOpen={setOpen}
        message={`Are you sure you want to delete '${
          listInfo?.data.title ?? ''
        }'? This action cannot be reversed.`}
        onCloseCallback={() => {}}
        onConfirm={() => {
          deleteList(listInfo?.data._id ?? '')?.then((resp) => {
            if (resp.data?.success) {
              navigate('/lists/curated')
            }
          })
        }}
        loading={deleting}
      />
    </Layout>
  )
}
